import React, { useEffect, useRef, useState } from 'react';
import {
  Building2,
  Save,
  Loader2,
  Upload,
  Image as ImageIcon,
  Trash2,
  Undo2,
  Check,
  X,
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useFacade, Facade, FacadeArea, FacadePoint } from '../../lib/hooks/useFacade';
import { useBuildingConfig } from '../../lib/hooks/useBuildingConfig';
import MediaPicker from '../../components/admin/MediaPicker';

const pointsToPath = (points: FacadePoint[]): string => {
  if (!points || points.length === 0) return '';
  return `M ${points[0].x} ${points[0].y} ${points
    .slice(1)
    .map((p) => `L ${p.x} ${p.y}`)
    .join(' ')} Z`;
};

const FacadeEditor = () => {
  const { facade, loading, saveFacade } = useFacade();
  const { config, getAvailableFloors, getEntranceLabel } = useBuildingConfig();

  const [imageUrl, setImageUrl] = useState('');
  const [areas, setAreas] = useState<FacadeArea[]>([]);
  const [drawing, setDrawing] = useState<FacadePoint[]>([]);
  const [entrance, setEntrance] = useState('');
  const [floor, setFloor] = useState<number>(1);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [showPicker, setShowPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  // The full list, plus which one is being edited. imageUrl/areas above stay
  // the working copy of the active facade so the drawing code is unchanged.
  const [facades, setFacades] = useState<Facade[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const imageRef = useRef<HTMLImageElement>(null);

  // Load persisted facades into local editing state.
  useEffect(() => {
    if (!loading) {
      setFacades(facade.facades);
      setActiveIndex(0);
      setImageUrl(facade.facades[0]?.imageUrl || '');
      setAreas(facade.facades[0]?.areas || []);
    }
  }, [facade, loading]);

  // The list with the working copy folded back into the active entry.
  const composeFacades = (): Facade[] => {
    if (facades.length === 0) {
      return imageUrl ? [{ id: 'facade-1', name: 'Фасада 1', imageUrl, areas }] : [];
    }
    return facades.map((f, i) => (i === activeIndex ? { ...f, imageUrl, areas } : f));
  };

  const openFacade = (index: number) => {
    const next = composeFacades();
    setFacades(next);
    setActiveIndex(index);
    setImageUrl(next[index]?.imageUrl || '');
    setAreas(next[index]?.areas || []);
    setDrawing([]);
    setDims({ width: 0, height: 0 });
  };

  const addFacade = () => {
    const next = composeFacades();
    next.push({
      id: `facade-${Date.now()}`,
      name: `Фасада ${next.length + 1}`,
      imageUrl: '',
      areas: [],
    });
    setFacades(next);
    setActiveIndex(next.length - 1);
    setImageUrl('');
    setAreas([]);
    setDrawing([]);
    setDims({ width: 0, height: 0 });
  };

  const renameFacade = (name: string) => {
    setFacades((prev) => {
      const next = prev.length ? [...prev] : composeFacades();
      if (next[activeIndex]) next[activeIndex] = { ...next[activeIndex], name };
      return next;
    });
  };

  const removeFacade = async (index: number) => {
    const list = composeFacades();
    if (!window.confirm(`Да изтрия ли „${list[index]?.name}" заедно с очертанията ѝ?`)) return;
    const next = list.filter((_, i) => i !== index);
    const newIndex = Math.max(0, Math.min(activeIndex, next.length - 1));
    setFacades(next);
    setActiveIndex(newIndex);
    setImageUrl(next[newIndex]?.imageUrl || '');
    setAreas(next[newIndex]?.areas || []);
    setDrawing([]);
    setDims({ width: 0, height: 0 });
    await saveFacade({ facades: next });
  };

  // Default the entrance/floor selectors once the building config is available.
  useEffect(() => {
    if (config.entrances.length && !entrance) {
      const first = config.entrances[0];
      setEntrance(first.id);
      setFloor(first.floors[0] ?? 1);
    }
  }, [config, entrance]);

  const availableFloors = getAvailableFloors(entrance);

  const handleImageLoad = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      setDims({ width: naturalWidth, height: naturalHeight });
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      const timestamp = Date.now();
      const safeName = encodeURIComponent(file.name).replace(/%/g, '_');
      const storageRef = ref(storage, `facade/facade_${timestamp}_${safeName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setImageUrl(url);
    } catch (err) {
      console.error('Error uploading facade image:', err);
      setStatus({ type: 'error', message: 'Грешка при качване на снимката' });
    } finally {
      setUploading(false);
    }
  };

  // Convert a click on the SVG into natural-image pixel coordinates.
  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dims.width) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * dims.width;
    const y = ((e.clientY - rect.top) / rect.height) * dims.height;
    setDrawing((prev) => [...prev, { x, y }]);
  };

  const undoPoint = () => setDrawing((prev) => prev.slice(0, -1));
  const clearDrawing = () => setDrawing([]);

  const finishArea = () => {
    if (drawing.length < 3) {
      setStatus({ type: 'error', message: 'Нужни са поне 3 точки за очертание' });
      return;
    }
    if (!entrance) {
      setStatus({ type: 'error', message: 'Изберете вход' });
      return;
    }
    const newArea: FacadeArea = {
      id: Date.now().toString(),
      entrance,
      floor,
      points: drawing,
      label: `Етаж ${floor}`,
    };
    setAreas((prev) => [...prev, newArea]);
    setDrawing([]);
    setStatus({ type: null, message: '' });
  };

  const removeArea = (id: string) => {
    setAreas((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatus({ type: null, message: '' });
    const ok = await saveFacade({ facades: composeFacades() });
    setIsSaving(false);
    setStatus(
      ok
        ? { type: 'success', message: 'Фасадата е запазена успешно!' }
        : { type: 'error', message: 'Грешка при запазване' }
    );
    if (ok) setTimeout(() => setStatus({ type: null, message: '' }), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const strokeW = dims.width ? dims.width * 0.0025 : 2;
  const dotR = dims.width ? dims.width * 0.006 : 4;

  return (
    <div className="p-6">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="flex items-center">
          <Building2 className="w-8 h-8 text-primary mr-3" />
          <h1 className="text-2xl font-bold">Фасада на сградата</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Запазване...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Запази промените
            </>
          )}
        </button>
      </div>

      {status.type && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          {status.message}
        </div>
      )}

      {/* Facade picker: several facades can be configured, each with its own
          image and outlines. The public section shows them as tabs. */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-1">Фасади</h2>
        <p className="text-sm text-gray-500 mb-4">
          Всяка фасада има собствена снимка и очертания. Ако са повече от една, на сайта се
          показват като табове. Не забравяйте да запазите промените.
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {composeFacades().map((item, index) => (
            <div
              key={item.id}
              className={`flex items-center rounded-lg border ${
                index === activeIndex ? 'border-primary bg-primary/5' : 'border-gray-200'
              }`}
            >
              <button
                onClick={() => openFacade(index)}
                className={`px-4 py-2 text-sm font-medium ${
                  index === activeIndex ? 'text-primary' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {item.name}
                {!item.imageUrl && <span className="ml-1 text-xs text-gray-400">(без снимка)</span>}
              </button>
              <button
                onClick={() => removeFacade(index)}
                className="px-2 py-2 text-gray-400 hover:text-red-600 transition-colors"
                title="Изтрий фасадата"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={addFacade}
            className="px-4 py-2 text-sm border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors"
          >
            + Добави фасада
          </button>
        </div>

        {composeFacades().length > 0 && (
          <div className="mb-6 max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Име на фасадата
            </label>
            <input
              type="text"
              value={composeFacades()[activeIndex]?.name || ''}
              onChange={(e) => renameFacade(e.target.value)}
              placeholder="напр. Южна фасада"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <p className="mt-1 text-xs text-gray-500">Показва се като надпис на таба.</p>
          </div>
        )}

        <h3 className="text-base font-semibold mb-4">Снимка на фасадата</h3>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="inline-flex items-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors"
          >
            <ImageIcon className="w-5 h-5 mr-2" />
            Избери от медийната библиотека
          </button>
          <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
            {uploading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Upload className="w-5 h-5 mr-2" />
            )}
            Качи нова снимка
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
              }}
            />
          </label>
          {imageUrl && (
            <button
              type="button"
              onClick={() => setImageUrl('')}
              className="inline-flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 mr-2" />
              Премахни снимката
            </button>
          )}
        </div>
      </div>

      {imageUrl ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Drawing canvas */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-4">
              <p className="text-sm text-gray-500 mb-3">
                Кликайте по снимката, за да поставяте точки около етажа. Нужни са поне 3 точки,
                след което натиснете „Завърши очертанието".
              </p>
              <div className="relative border rounded-lg overflow-hidden select-none">
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt="Фасада"
                  className="w-full h-auto block"
                  onLoad={handleImageLoad}
                  draggable={false}
                />
                {dims.width > 0 && (
                  <svg
                    className="absolute inset-0 w-full h-full cursor-crosshair"
                    viewBox={`0 0 ${dims.width} ${dims.height}`}
                    preserveAspectRatio="none"
                    onClick={handleSvgClick}
                  >
                    {/* Existing areas */}
                    {areas.map((area) => (
                      <path
                        key={area.id}
                        d={pointsToPath(area.points)}
                        style={{
                          fill:
                            hoveredArea === area.id
                              ? 'rgb(var(--color-secondary) / 0.45)'
                              : 'rgb(var(--color-primary) / 0.15)',
                          stroke: 'rgb(var(--color-secondary))',
                        }}
                        strokeWidth={strokeW}
                      />
                    ))}
                    {/* In-progress polygon */}
                    {drawing.length > 0 && (
                      <>
                        <polyline
                          points={drawing.map((p) => `${p.x},${p.y}`).join(' ')}
                          style={{ fill: 'none', stroke: 'rgb(var(--color-primary))' }}
                          strokeWidth={strokeW}
                          strokeDasharray={`${strokeW * 2},${strokeW * 2}`}
                        />
                        {drawing.map((p, i) => (
                          <circle
                            key={i}
                            cx={p.x}
                            cy={p.y}
                            r={dotR}
                            style={{ fill: 'rgb(var(--color-primary))' }}
                          />
                        ))}
                      </>
                    )}
                  </svg>
                )}
              </div>

              {/* Drawing controls */}
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Вход:</label>
                  <select
                    value={entrance}
                    onChange={(e) => {
                      setEntrance(e.target.value);
                      const floors = getAvailableFloors(e.target.value);
                      setFloor(floors[0] ?? 1);
                    }}
                    className="px-3 py-2 border rounded-lg bg-white"
                  >
                    {config.entrances.map((en) => (
                      <option key={en.id} value={en.id}>
                        {en.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Етаж:</label>
                  <select
                    value={floor}
                    onChange={(e) => setFloor(Number(e.target.value))}
                    className="px-3 py-2 border rounded-lg bg-white"
                  >
                    {availableFloors.map((f) => (
                      <option key={f} value={f}>
                        Етаж {f}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={undoPoint}
                  disabled={!drawing.length}
                  className="inline-flex items-center px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-40"
                >
                  <Undo2 className="w-4 h-4 mr-2" />
                  Върни точка
                </button>
                <button
                  type="button"
                  onClick={clearDrawing}
                  disabled={!drawing.length}
                  className="inline-flex items-center px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-40"
                >
                  <X className="w-4 h-4 mr-2" />
                  Изчисти
                </button>
                <button
                  type="button"
                  onClick={finishArea}
                  disabled={drawing.length < 3}
                  className="inline-flex items-center px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors disabled:opacity-40"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Завърши очертанието
                </button>
              </div>
            </div>
          </div>

          {/* Areas list */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="font-semibold mb-4">Очертани етажи ({areas.length})</h3>
              {areas.length === 0 ? (
                <p className="text-sm text-gray-500">Все още няма очертани етажи.</p>
              ) : (
                <div className="space-y-2">
                  {areas
                    .slice()
                    .sort((a, b) => a.floor - b.floor)
                    .map((area) => (
                      <div
                        key={area.id}
                        onMouseEnter={() => setHoveredArea(area.id)}
                        onMouseLeave={() => setHoveredArea(null)}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="text-sm">
                          <div className="font-medium">Етаж {area.floor}</div>
                          <div className="text-gray-500">
                            {getEntranceLabel(area.entrance)
                              ? `Вход ${getEntranceLabel(area.entrance)}`
                              : area.entrance}{' '}
                            • {area.points.length} точки
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeArea(area.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
              <p className="mt-4 text-xs text-gray-400">
                Не забравяйте да натиснете „Запази промените", за да ги приложите на сайта.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg p-10 text-center text-gray-500">
          Изберете или качете снимка на фасадата, за да започнете да очертавате етажите.
        </div>
      )}

      <MediaPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(url) => setImageUrl(url)}
        title="Избери снимка на фасадата"
      />
    </div>
  );
};

export default FacadeEditor;
